import { supabase } from '../lib/supabase';
import { jobIdService } from './jobIdService';

export const jobService = {
  // Get all jobs for a company
  async getCompanyJobs(companyId) {
    const { data, error } = await supabase?.from('jobs')?.select(`
        *,
        company_id,
        created_by,
        created_by_profile:user_profiles!jobs_created_by_fkey(id, full_name, email),
        service_receiver:service_receivers(id, receiver_name, receiver_type, contact_person),
        partner_agency:partner_agencies(id, agency_name, contact_person, contact_email, contact_phone),
        client:clients(id, client_name, contact_person, contact_email, contact_phone, tag),
        job_site:job_sites(id, site_name, address, address_lat, address_lng, contact_person, contact_email, contact_phone),
        job_instances(
          id,
          instance_number,
          scheduled_date,
          scheduled_time,
          scheduled_end_time,
          scheduled_duration,
          assigned_contractor,
          status,
          is_pending,
          is_pending_report,
          contractor:user_profiles!job_instances_assigned_contractor_fkey(id, full_name, email)
        )
      `)
      ?.eq('company_id', companyId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(job => ({
      id: job?.id,
      companyId: job?.company_id,
      createdBy: job?.created_by,
      createdByProfile: job?.created_by_profile,
      jobDisplayId: job?.job_display_id,
      jobName: job?.job_name,
      description: job?.description,
      jobType: job?.job_type,
      priority: job?.priority,
      status: job?.status,
      // New: Client / Job Site (replaces Partner Agency / Service Receiver)
      clientId: job?.client_id,
      client: job?.client ? {
        id: job?.client?.id,
        clientName: job?.client?.client_name,
        contactPerson: job?.client?.contact_person,
        contactEmail: job?.client?.contact_email,
        contactPhone: job?.client?.contact_phone,
        tag: job?.client?.tag
      } : null,
      jobSiteId: job?.job_site_id,
      jobSite: job?.job_site ? {
        id: job?.job_site?.id,
        siteName: job?.job_site?.site_name,
        address: job?.job_site?.address,
        addressLat: job?.job_site?.address_lat,
        addressLng: job?.job_site?.address_lng,
        contactPerson: job?.job_site?.contact_person,
        contactEmail: job?.job_site?.contact_email,
        contactPhone: job?.job_site?.contact_phone
      } : null,
      originalAddress: job?.original_address,
      // Legacy: Service receiver (backward compatibility)
      serviceReceiverId: job?.service_receiver_id,
      serviceReceiver: job?.service_receiver,
      isInternal: job?.is_internal,
      // Legacy: Partner Agency / Job Source (backward compatibility)
      jobSource: job?.job_source || 'direct',
      partnerAgencyId: job?.partner_agency_id,
      partnerAgency: job?.partner_agency,
      // Payment - contractor
      paymentType: job?.payment_type,
      paymentAmount: job?.payment_amount,
      // Payment - client
      clientChargeType: job?.client_charge_type,
      clientChargeAmount: job?.client_charge_amount,
      // GST settings
      clientIncludeGst: job?.client_include_gst ?? true,
      clientGstType: job?.client_gst_type || 'exclusive',
      contractorIncludeGst: job?.contractor_include_gst ?? false,
      contractorGstType: job?.contractor_gst_type || 'exclusive',
      // Location (use job_site address or fall back to location_address for legacy)
      locationAddress: job?.job_site?.address || job?.location_address,
      locationLat: job?.job_site?.address_lat || job?.location_lat,
      locationLng: job?.job_site?.address_lng || job?.location_lng,
      // Schedule
      recurringSchedule: job?.recurring_schedule,
      recurringEndDate: job?.recurring_end_date,
      estimatedHours: job?.estimated_hours,
      // Contact person
      contactPersonName: job?.contact_person_name,
      contactPersonPhone: job?.contact_person_phone,
      contactPersonEmail: job?.contact_person_email,
      // Requirements
      clockInRadius: job?.clock_in_radius,
      clockInFlexibility: job?.clock_in_flexibility,
      photosRequiredBefore: job?.photos_required_before,
      photosRequiredAfter: job?.photos_required_after,
      questionnaireTemplate: job?.questionnaire_template,
      safetyRequirements: job?.safety_requirements,
      equipmentList: job?.equipment_list,
      // Assignment
      isPublished: job?.is_published,
      // Other
      otherDetails: job?.other_details,
      cancellationReason: job?.cancellation_reason,
      createdAt: job?.created_at,
      updatedAt: job?.updated_at,
      // Instance Data: Find the most relevant instance
      // For One-Off: The single instance (or the one with the scheduled date)
      // We explicitly look for the first instance provided by the query
      latestInstanceId: job?.job_instances?.[0]?.id,
      scheduledDate: job?.job_instances?.[0]?.scheduled_date,
      scheduledTime: job?.job_instances?.[0]?.scheduled_time,
      assignedContractor: job?.job_instances?.[0]?.assigned_contractor ? (
        job?.job_instances?.[0]?.contractor ? {
          id: job?.job_instances?.[0]?.assigned_contractor, // Should Ideally be MemberID but we don't have it. Kept as UserID for fallback.
          userId: job?.job_instances?.[0]?.assigned_contractor,
          fullName: job?.job_instances?.[0]?.contractor?.full_name,
          email: job?.job_instances?.[0]?.contractor?.email,
          username: job?.job_instances?.[0]?.contractor?.username
        } : { id: job?.job_instances?.[0]?.assigned_contractor, fullName: 'Unknown', email: '' }
      ) : null,
      // Include raw job_instances for status computation
      job_instances: job?.job_instances
    }));
  },

  // Get a single job by ID
  async getJob(jobId) {
    const { data, error } = await supabase?.from('jobs')?.select(`
        *,
        created_by_profile:user_profiles!jobs_created_by_fkey(id, full_name, email),
        service_receiver:service_receivers(id, receiver_name, receiver_type, contact_person, contact_email, contact_phone, address),
        client:clients(id, client_name, contact_person, contact_email, contact_phone, address, tag, abn),
        job_site:job_sites(id, site_name, address, address_lat, address_lng, contact_person, contact_email, contact_phone)
      `)?.eq('id', jobId)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyId: data?.company_id,
      createdBy: data?.created_by,
      createdByProfile: data?.created_by_profile,
      jobDisplayId: data?.job_display_id,
      jobName: data?.job_name,
      description: data?.description,
      jobType: data?.job_type,
      priority: data?.priority,
      status: data?.status,
      // New: Client / Job Site
      clientId: data?.client_id,
      client: data?.client ? {
        id: data?.client?.id,
        clientName: data?.client?.client_name,
        contactPerson: data?.client?.contact_person,
        contactEmail: data?.client?.contact_email,
        contactPhone: data?.client?.contact_phone,
        address: data?.client?.address,
        tag: data?.client?.tag,
        abn: data?.client?.abn
      } : null,
      jobSiteId: data?.job_site_id,
      jobSite: data?.job_site ? {
        id: data?.job_site?.id,
        siteName: data?.job_site?.site_name,
        address: data?.job_site?.address,
        addressLat: data?.job_site?.address_lat,
        addressLng: data?.job_site?.address_lng,
        contactPerson: data?.job_site?.contact_person,
        contactEmail: data?.job_site?.contact_email,
        contactPhone: data?.job_site?.contact_phone
      } : null,
      originalAddress: data?.original_address,
      // Legacy: Service receiver (backward compatibility)
      serviceReceiverId: data?.service_receiver_id,
      serviceReceiver: data?.service_receiver,
      isInternal: data?.is_internal,
      // Legacy: Partner Agency / Job Source (backward compatibility)
      jobSource: data?.job_source || 'direct',
      partnerAgencyId: data?.partner_agency_id,
      // Payment
      paymentType: data?.payment_type,
      paymentAmount: data?.payment_amount,
      clientChargeType: data?.client_charge_type,
      clientChargeAmount: data?.client_charge_amount,
      clientIncludeGst: data?.client_include_gst ?? true,
      clientGstType: data?.client_gst_type || 'exclusive',
      contractorIncludeGst: data?.contractor_include_gst ?? false,
      contractorGstType: data?.contractor_gst_type || 'exclusive',
      // Location (use job_site address or fall back to location_address for legacy)
      locationAddress: data?.job_site?.address || data?.location_address,
      locationLat: data?.job_site?.address_lat || data?.location_lat,
      locationLng: data?.job_site?.address_lng || data?.location_lng,
      recurringSchedule: data?.recurring_schedule,
      recurringEndDate: data?.recurring_end_date,
      estimatedHours: data?.estimated_hours,
      contactPersonName: data?.contact_person_name,
      contactPersonPhone: data?.contact_person_phone,
      contactPersonEmail: data?.contact_person_email,
      clockInRadius: data?.clock_in_radius,
      clockInFlexibility: data?.clock_in_flexibility,
      lateSubmissionWindowHours: data?.late_submission_window_hours,
      photosRequiredBefore: data?.photos_required_before,
      photosRequiredAfter: data?.photos_required_after,
      questionnaireTemplate: data?.questionnaire_template,
      safetyRequirements: data?.safety_requirements,
      equipmentList: data?.equipment_list,
      isPublished: data?.is_published,
      otherDetails: data?.other_details,
      cancellationReason: data?.cancellation_reason,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Search job names for autocomplete
  async searchJobNames(companyId, searchTerm) {
    const { data, error } = await supabase
      ?.from('jobs')
      ?.select('job_name')
      ?.eq('company_id', companyId)
      ?.ilike('job_name', `${searchTerm}%`)
      ?.order('job_name', { ascending: true })
      ?.limit(10);

    if (error) throw error;

    // Return unique job names
    const uniqueNames = [...new Set(data?.map(j => j?.job_name))];
    return uniqueNames;
  },

  // Create new job with all enhanced fields
  async createJob(jobData, companyId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Generate the display ID
    const jobDisplayId = await jobIdService.getNextJobId(companyId);

    const { data, error } = await supabase?.from('jobs')?.insert({
      company_id: companyId,
      created_by: user?.id,
      job_display_id: jobDisplayId,
      job_name: jobData?.jobName,
      description: jobData?.description,
      job_type: jobData?.jobType || 'one_off',
      priority: jobData?.priority || 'medium',
      status: jobData?.status || 'draft',
      // New: Client / Job Site
      client_id: jobData?.clientId || null,
      job_site_id: jobData?.jobSiteId || null,
      original_address: jobData?.originalAddress || null,
      // Legacy: Service receiver (backward compatibility)
      service_receiver_id: jobData?.serviceReceiverId,
      is_internal: jobData?.isInternal || false,
      // Legacy: Partner Agency / Job Source (backward compatibility)
      job_source: jobData?.jobSource || 'direct',
      partner_agency_id: jobData?.partnerAgencyId || null,
      // Payment - contractor
      payment_type: jobData?.paymentType || 'hourly',
      payment_amount: jobData?.paymentAmount,
      // Payment - client
      client_charge_type: jobData?.clientChargeType || 'hourly',
      client_charge_amount: jobData?.clientChargeAmount,
      // GST settings
      client_include_gst: jobData?.clientIncludeGst ?? true,
      client_gst_type: jobData?.clientGstType || 'exclusive',
      contractor_include_gst: jobData?.contractorIncludeGst ?? false,
      contractor_gst_type: jobData?.contractorGstType || 'exclusive',
      // Location (legacy - use original_address for new jobs if job_site is set)
      location_address: jobData?.locationAddress,
      location_lat: jobData?.locationLat,
      location_lng: jobData?.locationLng,
      // Schedule
      recurring_schedule: jobData?.recurringSchedule,
      recurring_end_date: jobData?.recurringEndDate,
      estimated_hours: jobData?.estimatedHours,
      // Contact person
      contact_person_name: jobData?.contactPersonName,
      contact_person_phone: jobData?.contactPersonPhone,
      contact_person_email: jobData?.contactPersonEmail,
      // Requirements
      clock_in_radius: jobData?.clockInRadius,
      clock_in_flexibility: jobData?.clockInFlexibility || false,
      late_submission_window_hours: jobData?.lateSubmissionWindowHours || 24,
      photos_required_before: jobData?.photosRequiredBefore !== false, // Default true
      photos_required_after: jobData?.photosRequiredAfter !== false, // Default true
      questionnaire_template: jobData?.questionnaireTemplate,
      safety_requirements: jobData?.safetyRequirements,
      equipment_list: jobData?.equipmentList,
      // Assignment
      is_published: jobData?.isPublished || false,
      // Other
      other_details: jobData?.otherDetails
    })?.select()?.single();

    if (error) throw error;

    return {
      ...data,
      jobDisplayId: data?.job_display_id,
      jobName: data?.job_name,
      clientId: data?.client_id,
      jobSiteId: data?.job_site_id,
      originalAddress: data?.original_address
    };
  },

  // Update job with all fields
  async updateJob(jobId, updates) {
    const updateData = {};

    // Only include fields that are being updated
    if (updates?.jobName !== undefined) updateData.job_name = updates.jobName;
    if (updates?.description !== undefined) updateData.description = updates.description;
    if (updates?.jobType !== undefined) updateData.job_type = updates.jobType;
    if (updates?.priority !== undefined) updateData.priority = updates.priority;
    if (updates?.status !== undefined) updateData.status = updates.status;
    // New: Client / Job Site
    if (updates?.clientId !== undefined) updateData.client_id = updates.clientId;
    if (updates?.jobSiteId !== undefined) updateData.job_site_id = updates.jobSiteId;
    if (updates?.originalAddress !== undefined) updateData.original_address = updates.originalAddress;
    // Legacy: Service receiver (backward compatibility)
    if (updates?.serviceReceiverId !== undefined) updateData.service_receiver_id = updates.serviceReceiverId;
    if (updates?.isInternal !== undefined) updateData.is_internal = updates.isInternal;
    // Legacy: Partner Agency / Job Source (backward compatibility)
    if (updates?.jobSource !== undefined) updateData.job_source = updates.jobSource;
    if (updates?.partnerAgencyId !== undefined) updateData.partner_agency_id = updates.partnerAgencyId;
    if (updates?.paymentType !== undefined) updateData.payment_type = updates.paymentType;
    if (updates?.paymentAmount !== undefined) updateData.payment_amount = updates.paymentAmount;
    if (updates?.clientChargeType !== undefined) updateData.client_charge_type = updates.clientChargeType;
    if (updates?.clientChargeAmount !== undefined) updateData.client_charge_amount = updates.clientChargeAmount;
    if (updates?.clientIncludeGst !== undefined) updateData.client_include_gst = updates.clientIncludeGst;
    if (updates?.clientGstType !== undefined) updateData.client_gst_type = updates.clientGstType;
    if (updates?.contractorIncludeGst !== undefined) updateData.contractor_include_gst = updates.contractorIncludeGst;
    if (updates?.contractorGstType !== undefined) updateData.contractor_gst_type = updates.contractorGstType;
    if (updates?.locationAddress !== undefined) updateData.location_address = updates.locationAddress;
    if (updates?.locationLat !== undefined) updateData.location_lat = updates.locationLat;
    if (updates?.locationLng !== undefined) updateData.location_lng = updates.locationLng;
    if (updates?.recurringSchedule !== undefined) updateData.recurring_schedule = updates.recurringSchedule;
    if (updates?.recurringEndDate !== undefined) updateData.recurring_end_date = updates.recurringEndDate;
    if (updates?.estimatedHours !== undefined) updateData.estimated_hours = updates.estimatedHours;
    if (updates?.contactPersonName !== undefined) updateData.contact_person_name = updates.contactPersonName;
    if (updates?.contactPersonPhone !== undefined) updateData.contact_person_phone = updates.contactPersonPhone;
    if (updates?.contactPersonEmail !== undefined) updateData.contact_person_email = updates.contactPersonEmail;
    if (updates?.clockInRadius !== undefined) updateData.clock_in_radius = updates.clockInRadius;
    if (updates?.clockInFlexibility !== undefined) updateData.clock_in_flexibility = updates.clockInFlexibility;
    if (updates?.lateSubmissionWindowHours !== undefined) updateData.late_submission_window_hours = updates.lateSubmissionWindowHours;
    if (updates?.photosRequiredBefore !== undefined) updateData.photos_required_before = updates.photosRequiredBefore;
    if (updates?.photosRequiredAfter !== undefined) updateData.photos_required_after = updates.photosRequiredAfter;
    if (updates?.questionnaireTemplate !== undefined) updateData.questionnaire_template = updates.questionnaireTemplate;
    if (updates?.safetyRequirements !== undefined) updateData.safety_requirements = updates.safetyRequirements;
    if (updates?.equipmentList !== undefined) updateData.equipment_list = updates.equipmentList;
    if (updates?.isPublished !== undefined) updateData.is_published = updates.isPublished;
    if (updates?.otherDetails !== undefined) updateData.other_details = updates.otherDetails;
    if (updates?.cancellationReason !== undefined) updateData.cancellation_reason = updates.cancellationReason;

    const { data, error } = await supabase?.from('jobs')?.update(updateData)
      ?.eq('id', jobId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Delete job
  async deleteJob(jobId) {
    const { error } = await supabase?.from('jobs')?.delete()?.eq('id', jobId);

    if (error) throw error;
  },

  // Get job instances for a job
  async getJobInstances(jobId) {
    const { data, error } = await supabase?.from('job_instances')?.select(`
        *,
        job:jobs(*),
        contractor:user_profiles!job_instances_assigned_contractor_fkey(id, full_name, email)
      `)?.eq('job_id', jobId)?.order('scheduled_date', { ascending: true });

    if (error) throw error;

    return data?.map(instance => ({
      id: instance?.id,
      jobId: instance?.job_id,
      job: instance?.job,
      companyId: instance?.company_id,
      instanceDisplayId: instance?.instance_display_id,
      instanceNumber: instance?.instance_number,
      assignedContractor: instance?.assigned_contractor,
      contractor: instance?.contractor,
      scheduledDate: instance?.scheduled_date,
      scheduledTime: instance?.scheduled_time,
      status: instance?.status,
      clockInTime: instance?.clock_in_time,
      clockOutTime: instance?.clock_out_time,
      workNotes: instance?.work_notes,
      questionnaireResponses: instance?.questionnaire_responses,
      checklistData: instance?.checklist_data,
      createdAt: instance?.created_at
    }));
  },

  // Assign contractor to job instance
  async assignContractor(instanceId, contractorId) {
    console.log('=== jobService.assignContractor ===');
    console.log('instanceId:', instanceId);
    console.log('contractorId:', contractorId);

    const { data, error } = await supabase?.from('job_instances')?.update({
      assigned_contractor: contractorId,
      status: 'assigned'
    })?.eq('id', instanceId)?.select()?.single();

    console.log('Update result:', { data, error });

    if (error) {
      console.error('Error assigning contractor:', error);
      throw error;
    }

    // Verify the update was applied
    if (data?.status !== 'assigned') {
      console.error('WARNING: Status was not set to assigned!', data);
    }

    return data;
  },

  // Create job instance with display ID
  async createJobInstance(jobId, companyId, scheduledDate, scheduledTime, assignedContractor = null) {
    // Get the job to retrieve its display ID
    const { data: job } = await supabase
      ?.from('jobs')
      ?.select('job_display_id')
      ?.eq('id', jobId)
      ?.single();

    // Get next instance number
    const instanceNumber = await jobIdService.getNextInstanceNumber(jobId);
    const instanceDisplayId = jobIdService.generateInstanceDisplayId(job?.job_display_id, instanceNumber);

    const insertData = {
      job_id: jobId,
      company_id: companyId,
      scheduled_date: scheduledDate,
      scheduled_time: scheduledTime,
      instance_display_id: instanceDisplayId,
      instance_number: instanceNumber,
      status: assignedContractor ? 'assigned' : 'unassigned'
    };

    if (assignedContractor) {
      insertData.assigned_contractor = assignedContractor;
    }

    const { data, error } = await supabase?.from('job_instances')?.insert(insertData)
      ?.select()?.single();

    if (error) throw error;

    return {
      ...data,
      instanceDisplayId: data?.instance_display_id,
      instanceNumber: data?.instance_number
    };
  },

  // Helper function to calculate duration in minutes
  _calculateDurationMinutes(startTime, endTime) {
    if (!startTime || !endTime) return null;

    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) {
      return null;
    }

    let durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);

    // Handle overnight shifts (e.g., 22:00 - 06:00)
    if (durationMinutes < 0) {
      durationMinutes += 24 * 60;
    }

    return durationMinutes;
  },

  // Create multiple job instances for recurring jobs
  async createRecurringInstances(jobId, companyId, jobDisplayId, schedule, numberOfInstances = 4) {
    const instances = [];
    // Support both old format (time, customTimes) and new format (startTime, endTime, customSchedule)
    const { frequency, days, time, pattern, customTimes, startTime, endTime, customSchedule } = schedule || {};

    if (!days || days.length === 0) {
      throw new Error('Recurring schedule must have at least one day selected');
    }

    // Map day names to day numbers (0 = Sunday, 1 = Monday, etc.)
    const dayMap = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
    // Reverse map: day number to day key
    const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const targetDays = days?.map(d => dayMap[d?.toLowerCase()]).filter(d => d !== undefined);

    let currentDate = new Date();
    let instancesCreated = 0;
    let instanceNumber = await jobIdService.getNextInstanceNumber(jobId);

    // Create instances for the next N occurrences
    while (instancesCreated < numberOfInstances) {
      const dayOfWeek = currentDate.getDay();

      if (targetDays.includes(dayOfWeek)) {
        const instanceDisplayId = jobIdService.generateInstanceDisplayId(jobDisplayId, instanceNumber);
        const dayKey = dayKeys[dayOfWeek];

        // Get the correct start time, end time, and duration for this day
        let instanceStartTime = null;
        let instanceEndTime = null;
        let instanceDuration = null;

        if (pattern === 'custom') {
          // New format: customSchedule with { startTime, endTime } per day
          if (customSchedule?.[dayKey]) {
            instanceStartTime = customSchedule[dayKey].startTime || null;
            instanceEndTime = customSchedule[dayKey].endTime || null;
          }
          // Old format fallback: customTimes with single time per day
          else if (customTimes?.[dayKey]) {
            instanceStartTime = customTimes[dayKey];
          }
        } else {
          // Fixed pattern
          // New format: startTime and endTime
          if (startTime) {
            instanceStartTime = startTime;
            instanceEndTime = endTime || null;
          }
          // Old format fallback: single time field
          else if (time) {
            instanceStartTime = time;
          }
        }

        // Calculate duration if we have both start and end times
        if (instanceStartTime && instanceEndTime) {
          instanceDuration = this._calculateDurationMinutes(instanceStartTime, instanceEndTime);
        }

        instances.push({
          job_id: jobId,
          company_id: companyId,
          scheduled_date: currentDate.toISOString().split('T')[0],
          scheduled_time: instanceStartTime,
          scheduled_end_time: instanceEndTime,
          scheduled_duration: instanceDuration,
          instance_display_id: instanceDisplayId,
          instance_number: instanceNumber,
          status: 'unassigned'
        });

        instanceNumber++;
        instancesCreated++;
      }

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);

      // Safety limit - don't go more than 365 days out
      if (currentDate > new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)) {
        break;
      }
    }

    if (instances.length === 0) {
      return [];
    }

    const { data, error } = await supabase?.from('job_instances')?.insert(instances)?.select();

    if (error) throw error;

    return data?.map(instance => ({
      id: instance?.id,
      instanceDisplayId: instance?.instance_display_id,
      instanceNumber: instance?.instance_number,
      scheduledDate: instance?.scheduled_date,
      scheduledTime: instance?.scheduled_time,
      scheduledEndTime: instance?.scheduled_end_time,
      scheduledDuration: instance?.scheduled_duration,
      status: instance?.status
    }));
  },

  // Create recurring instances starting from a specific date (for adding more instances to existing recurring jobs)
  async createRecurringInstancesFromDate(jobId, companyId, jobDisplayId, schedule, numberOfInstances = 5, startAfterDate = null, endDate = null) {
    const instances = [];
    // Support both old format (time, customTimes) and new format (startTime, endTime, customSchedule)
    const { frequency, days, time, pattern, customTimes, startTime, endTime, customSchedule } = schedule || {};

    if (!days || days.length === 0) {
      throw new Error('Recurring schedule must have at least one day selected');
    }

    // Map day names to day numbers (0 = Sunday, 1 = Monday, etc.)
    const dayMap = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
    // Reverse map: day number to day key
    const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const targetDays = days?.map(d => dayMap[d?.toLowerCase()]).filter(d => d !== undefined);

    // Start from the day after the last instance, or today if no last instance
    let currentDate = startAfterDate ? new Date(startAfterDate) : new Date();
    currentDate.setDate(currentDate.getDate() + 1); // Start from the next day

    // Parse end date if provided
    const maxEndDate = endDate ? new Date(endDate) : null;

    let instancesCreated = 0;
    let instanceNumber = await jobIdService.getNextInstanceNumber(jobId);

    // Create instances for the next N occurrences
    while (instancesCreated < numberOfInstances) {
      // Check if we've passed the end date
      if (maxEndDate && currentDate > maxEndDate) {
        break;
      }

      const dayOfWeek = currentDate.getDay();

      if (targetDays.includes(dayOfWeek)) {
        const instanceDisplayId = jobIdService.generateInstanceDisplayId(jobDisplayId, instanceNumber);
        const dayKey = dayKeys[dayOfWeek];

        // Get the correct start time, end time, and duration for this day
        let instanceStartTime = null;
        let instanceEndTime = null;
        let instanceDuration = null;

        if (pattern === 'custom') {
          // New format: customSchedule with { startTime, endTime } per day
          if (customSchedule?.[dayKey]) {
            instanceStartTime = customSchedule[dayKey].startTime || null;
            instanceEndTime = customSchedule[dayKey].endTime || null;
          }
          // Old format fallback: customTimes with single time per day
          else if (customTimes?.[dayKey]) {
            instanceStartTime = customTimes[dayKey];
          }
        } else {
          // Fixed pattern
          // New format: startTime and endTime
          if (startTime) {
            instanceStartTime = startTime;
            instanceEndTime = endTime || null;
          }
          // Old format fallback: single time field
          else if (time) {
            instanceStartTime = time;
          }
        }

        // Calculate duration if we have both start and end times
        if (instanceStartTime && instanceEndTime) {
          instanceDuration = this._calculateDurationMinutes(instanceStartTime, instanceEndTime);
        }

        instances.push({
          job_id: jobId,
          company_id: companyId,
          scheduled_date: currentDate.toISOString().split('T')[0],
          scheduled_time: instanceStartTime,
          scheduled_end_time: instanceEndTime,
          scheduled_duration: instanceDuration,
          instance_display_id: instanceDisplayId,
          instance_number: instanceNumber,
          status: 'unassigned'
        });

        instanceNumber++;
        instancesCreated++;
      }

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);

      // Safety limit - don't go more than 365 days out
      if (currentDate > new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)) {
        break;
      }
    }

    if (instances.length === 0) {
      return [];
    }

    const { data, error } = await supabase?.from('job_instances')?.insert(instances)?.select();

    if (error) throw error;

    return data?.map(instance => ({
      id: instance?.id,
      instanceDisplayId: instance?.instance_display_id,
      instanceNumber: instance?.instance_number,
      scheduledDate: instance?.scheduled_date,
      scheduledTime: instance?.scheduled_time,
      scheduledEndTime: instance?.scheduled_end_time,
      scheduledDuration: instance?.scheduled_duration,
      status: instance?.status
    }));
  },

  // Update job instance status (status can be null to only update additionalData)
  async updateJobInstanceStatus(instanceId, status, additionalData = {}) {
    console.log('=== jobService.updateJobInstanceStatus ===');
    console.log('instanceId:', instanceId);
    console.log('status:', status);
    console.log('additionalData:', additionalData);

    const updateData = { ...additionalData };

    // Only include status if it's provided (not null/undefined)
    if (status !== null && status !== undefined) {
      updateData.status = status;
    }

    console.log('Final updateData:', updateData);

    const { data, error } = await supabase?.from('job_instances')
      ?.update(updateData)
      ?.eq('id', instanceId)
      ?.select()
      ?.single();

    console.log('updateJobInstanceStatus result:', { data, error });

    if (error) {
      console.error('Error updating job instance status:', error);
      throw error;
    }
    return data;
  },

  // Delete a job instance
  async deleteJobInstance(instanceId) {
    const { error } = await supabase
      ?.from('job_instances')
      ?.delete()
      ?.eq('id', instanceId);

    if (error) throw error;
  },

  // Get public (published) jobs for contractors
  async getPublishedJobs(companyId) {
    const { data, error } = await supabase?.from('jobs')?.select(`
        *,
        service_receiver:service_receivers(id, receiver_name),
        client:clients(id, client_name),
        job_site:job_sites(id, site_name, address)
      `)
      ?.eq('company_id', companyId)
      ?.eq('is_published', true)
      ?.eq('status', 'active')
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(job => ({
      id: job?.id,
      jobDisplayId: job?.job_display_id,
      jobName: job?.job_name,
      description: job?.description,
      priority: job?.priority,
      // Location: prefer job_site address, fall back to location_address
      locationAddress: job?.job_site?.address || job?.location_address,
      paymentType: job?.payment_type,
      paymentAmount: job?.payment_amount,
      estimatedHours: job?.estimated_hours,
      // New: Client / Job Site
      clientId: job?.client_id,
      client: job?.client,
      jobSiteId: job?.job_site_id,
      jobSite: job?.job_site,
      // Legacy: Service receiver (backward compatibility)
      serviceReceiver: job?.service_receiver
    }));
  },

  // Get single job instance by ID
  async getJobInstance(instanceId) {
    const { data, error } = await supabase?.from('job_instances')
      ?.select('id, scheduled_date, scheduled_time, status, job_id')
      ?.eq('id', instanceId)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      scheduledDate: data?.scheduled_date,
      scheduledTime: data?.scheduled_time,
      status: data?.status,
      jobId: data?.job_id
    };
  },

  // ============================================
  // Extension Request Management (Corporate)
  // ============================================

  /**
   * Get all pending extension requests for a company
   * @param {string} companyId - Company ID
   * @returns {Promise<Array>} Array of extension requests with job/contractor details
   */
  async getExtensionRequests(companyId, status = null) {
    try {
      let query = supabase
        ?.from('job_extension_requests')
        ?.select(`
          *,
          contractor:contractor_id (id, full_name, email),
          job_instance:job_instance_id (
            id,
            scheduled_date,
            scheduled_time,
            instance_number,
            status,
            submission_deadline,
            jobs:job_id (id, job_name, job_display_id)
          )
        `)
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });

      if (status) {
        query = query?.eq('status', status);
      }

      const { data, error } = await query;

      // If table doesn't exist, return empty array
      if (error && (error.code === '42P01' || error.message?.includes('does not exist'))) {
        console.warn('job_extension_requests table does not exist yet. Run migration to enable this feature.');
        return [];
      }

      if (error) throw error;

      return data || [];
    } catch (err) {
      // Handle case where table doesn't exist
      if (err.message?.includes('does not exist') || err.code === '42P01') {
        console.warn('job_extension_requests table does not exist yet. Run migration to enable this feature.');
        return [];
      }
      throw err;
    }
  },

  /**
   * Review (approve/deny) an extension request
   * @param {string} requestId - Extension request ID
   * @param {boolean} approved - Whether to approve or deny
   * @param {string} reviewNote - Optional note for contractor
   * @returns {Promise<Object>} Updated request
   */
  async reviewExtensionRequest(requestId, approved, reviewNote = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get the request to access the job_instance_id
    const { data: request, error: fetchError } = await supabase
      ?.from('job_extension_requests')
      ?.select('id, job_instance_id, requested_deadline')
      ?.eq('id', requestId)
      ?.single();

    if (fetchError) throw fetchError;
    if (!request) throw new Error('Extension request not found');

    // Update the extension request
    const { data: updatedRequest, error: updateError } = await supabase
      ?.from('job_extension_requests')
      ?.update({
        status: approved ? 'approved' : 'denied',
        reviewed_by: user?.id,
        reviewed_at: new Date().toISOString(),
        review_note: reviewNote,
        updated_at: new Date().toISOString()
      })
      ?.eq('id', requestId)
      ?.select()
      ?.single();

    if (updateError) throw updateError;

    // If approved, update the job instance deadline
    if (approved) {
      // Get current deadline for audit log
      const { data: instance } = await supabase
        ?.from('job_instances')
        ?.select('submission_deadline')
        ?.eq('id', request.job_instance_id)
        ?.single();

      // Update the job instance with new deadline
      const { error: instanceError } = await supabase
        ?.from('job_instances')
        ?.update({
          submission_deadline: request.requested_deadline,
          deadline_extended_by: user?.id,
          deadline_extension_reason: reviewNote || 'Extension request approved'
        })
        ?.eq('id', request.job_instance_id);

      if (instanceError) throw instanceError;

      // Create audit log entry
      await supabase
        ?.from('job_deadline_audit')
        ?.insert({
          job_instance_id: request.job_instance_id,
          changed_by: user?.id,
          previous_deadline: instance?.submission_deadline,
          new_deadline: request.requested_deadline,
          change_type: 'request_approved',
          reason: reviewNote || 'Extension request approved'
        });
    } else {
      // Log denial
      await supabase
        ?.from('job_deadline_audit')
        ?.insert({
          job_instance_id: request.job_instance_id,
          changed_by: user?.id,
          previous_deadline: null,
          new_deadline: null,
          change_type: 'request_denied',
          reason: reviewNote || 'Extension request denied'
        });
    }

    return updatedRequest;
  },

  /**
   * Manually extend a job instance deadline (without a request)
   * @param {string} instanceId - Job instance ID
   * @param {Date} newDeadline - New deadline date/time
   * @param {string} reason - Reason for extension
   * @returns {Promise<Object>} Updated job instance
   */
  async extendInstanceDeadline(instanceId, newDeadline, reason) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get current deadline for audit log
    const { data: instance, error: fetchError } = await supabase
      ?.from('job_instances')
      ?.select('submission_deadline')
      ?.eq('id', instanceId)
      ?.single();

    if (fetchError) throw fetchError;

    // Update the job instance
    const { data, error } = await supabase
      ?.from('job_instances')
      ?.update({
        submission_deadline: newDeadline.toISOString(),
        allow_late_submission_override: true,
        deadline_extended_by: user?.id,
        deadline_extension_reason: reason
      })
      ?.eq('id', instanceId)
      ?.select()
      ?.single();

    if (error) throw error;

    // Create audit log entry
    await supabase
      ?.from('job_deadline_audit')
      ?.insert({
        job_instance_id: instanceId,
        changed_by: user?.id,
        previous_deadline: instance?.submission_deadline,
        new_deadline: newDeadline.toISOString(),
        change_type: 'manual_extension',
        reason: reason
      });

    return data;
  },

  /**
   * Get deadline audit history for a job instance
   * @param {string} instanceId - Job instance ID
   * @returns {Promise<Array>} Array of audit entries
   */
  async getDeadlineAuditHistory(instanceId) {
    const { data, error } = await supabase
      ?.from('job_deadline_audit')
      ?.select(`
        *,
        changed_by_user:changed_by (id, full_name, email)
      `)
      ?.eq('job_instance_id', instanceId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Update pending job flags for all overdue instances
   * This calls the database function that sets is_pending and is_pending_report flags
   * Should be called on page load to ensure flags are current
   * @param {string} companyId - Company ID to update flags for
   * @returns {Promise<Object>} Result with counts of updated instances
   */
  async updatePendingJobFlags(companyId) {
    const { data, error } = await supabase
      ?.rpc('update_pending_job_flags', { p_company_id: companyId });

    if (error) {
      console.error('Failed to update pending job flags:', error);
      // Don't throw - this is a non-critical operation
      return { pendingCount: 0, pendingReportCount: 0 };
    }

    return data || { pendingCount: 0, pendingReportCount: 0 };
  }
};
